import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const serviceLinks = [
  { label: 'Website Design', href: '/website-design' },
  { label: 'Videography', href: '/videography' },
  { label: 'Video Editing', href: '/video-editing' },
  { label: 'Ethical Hacking', href: '/ethical-hacking' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/d7da4bfde00f775727e3be13864eec4a.png"
              alt="Bellport Branding"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer">
              About
            </a>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-xl border border-neutral-100 py-2 z-50">
                  {serviceLinks.map((s) => (
                    <Link key={s.href} to={s.href} onClick={() => setServicesOpen(false)} className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors cursor-pointer">
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/portfolio" className="text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer">
              Portfolio
            </Link>

            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer">
              Contact
            </a>

            <a href="tel:+16316407888" className="text-sm font-medium text-gray-700 hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer">
              (631) 640-7888
            </a>
            <a
              href="#contact"
              className="px-6 py-2.5 bg-[#1E3A8A] text-white text-sm font-medium rounded-full hover:bg-[#1E3A8A]/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              Free Consultation
            </a>
          </div>

          {/* Mobile Toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-neutral-700 cursor-pointer">
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-neutral-100">
          <div className="px-6 py-4 space-y-3">
            <a href="tel:+16316407888" className="flex items-center gap-2 text-lg font-semibold text-[#1E3A8A] py-2 cursor-pointer">
              <i className="ri-phone-fill"></i>(631) 640-7888
            </a>
            <Link to="/" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer">Home</Link>
            <a href="#about" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer">About</a>
            <div>
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="flex items-center gap-1 w-full text-left text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer">
                Services <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  {serviceLinks.map((s) => (
                    <Link key={s.href} to={s.href} onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-500 hover:text-neutral-900 py-1.5 cursor-pointer">{s.label}</Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/portfolio" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer">Portfolio</Link>
            <a href="#contact" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer">Contact</a>
            <a href="#contact" onClick={() => setMobileOpen(false)} className="block w-full bg-[#1E3A8A] text-white px-5 py-3 rounded-full text-sm font-bold text-center cursor-pointer whitespace-nowrap mt-4">Free Consultation</a>
          </div>
        </div>
      )}
    </nav>
  );
}